import { useState, useCallback, useMemo } from "react";
import {
  ContextualSaveBar,
  Page,
  Layout,
  Card,
  BlockStack,
  Loading,
} from "@medentic/polaris-react";
import { DeleteConfirmationModal } from "@shared/ui";
import { ExternalIcon, DeleteIcon } from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { AppPageWrapper } from "@shared/ui";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { Instructor } from "@features/events/types";
import { NoteTone } from "@shared/ui";
import useEventMetadata from "@features/events/hooks/useEventMetadata";
import usePageMeta from "@shared/hooks/common/usePageMeta";
import { getLocalizedText } from "@shared/lib";
import { showToast } from "@shared/lib";
import { RootState } from "@app/store/store";
import { useInstructorValidation } from "./components/useInstructorValidation";

import {
  InstructorDetailsSkeleton,
  OverviewCard,
  InstructorStatusCard,
  InstructorTranslationsCard,
  ContactInformationCard,
  CourseLanguageSelectionCard,
  ProfileButtonCard,
  AffiliatedBrandsCard,
  InstructorNotes,
  ProfileImageAndMediaCard,
  SocialMediaProfilesCard,
} from "./components";
import { TranslationsEntityType } from "@features/events/components/TranslationsCard";

const NEW_INSTRUCTOR_INITIAL_DATA: Partial<Instructor> = {
  title: "doctor",
  firstname: {},
  lastname: {},
  headline: {},
  bio: {},
  email: undefined,
  phone: undefined,
  phoneCountryCode: "+359",
  currentPosition: undefined,
  currentCompany: undefined,
  slug: undefined,
  visibility: 1,
  verified: false,
  featured: false,
};

const mapInstructorToDraftData = (
  instructor: Instructor | undefined
): Partial<Instructor> => {
  if (!instructor) {
    return {};
  }

  return {
    id: instructor.id,
    title: instructor.title,
    firstname: instructor.firstname,
    lastname: instructor.lastname,
    headline: instructor.headline,
    bio: instructor.bio,
    email: instructor.email,
    phone: instructor.phone,
    phoneCountryCode: instructor.phoneCountryCode,
    currentPosition: instructor.currentPosition,
    currentCompany: instructor.currentCompany,
    slug: instructor.slug,
    visibility: instructor.visibility,
    verified: instructor.verified,
    featured: instructor.featured,
    languageCodes: instructor.languageCodes,
    geoAddress: instructor.geoAddress,
    categoryIds: instructor.categoryIds,
    social: instructor.social,
    customButtonLabel: instructor.customButtonLabel,
    customButtonLink: instructor.customButtonLink,
    avatar: instructor.avatar,
    media: instructor.media,
    privateNote: instructor.privateNote,
    privateNoteTone: instructor.privateNoteTone,
    brandIds: instructor.brandIds,
    nameWithPrefix: instructor.nameWithPrefix,
  };
};

export default function InstructorDetails() {
  const { t } = useTranslation();
  const { eventOrganizerId: hcc, instructorId } = useParams<{
    eventOrganizerId: string;
    instructorId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { GET, POST, PUT, DELETE } = useApiRequest();
  const queryClient = useQueryClient();
  const { toneOptions } = useEventMetadata();
  const { metadata } = useEventMetadata();
  const dispatch = useDispatch();
  const selectedLanguages = useSelector(
    (state: RootState) => state.event.selectedLanguages
  );
  // Determine if we're creating a new instructor
  const isNewInstructor = instructorId === "new";

  // State
  const [instructorDataDraft, setInstructorDataDraft] = useState<
    Partial<Instructor> | null
  >(isNewInstructor ? NEW_INSTRUCTOR_INITIAL_DATA : null);

  const [isDirty, setIsDirty] = useState(isNewInstructor); // New instructors start as dirty
  const [translationsExpanded, setTranslationsExpanded] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Use validation hook
  const {
    errors: fieldErrors,
    validate,
    validateField,
    setFieldError,
    clearErrors,
  } = useInstructorValidation();

  // Fetch instructor data (only for existing instructors)
  const {
    data: instructor,
    isLoading,
    isFetching,
  } = useQuery<Instructor>({
    queryKey: ["instructor", hcc, instructorId],
    queryFn: () =>
      GET(`/api/healthcare-companies/${hcc}/e/instructors/${instructorId}`),
    enabled: Boolean(hcc && instructorId && !isNewInstructor),
  });

  // Prepare page meta data
  const instructorFirstName = getLocalizedText(instructor?.firstname);
  const instructorLastName = getLocalizedText(instructor?.lastname);
  const instructorName =
    instructorFirstName && instructorLastName
      ? `${instructorFirstName} ${instructorLastName}`.trim()
      : undefined;
  const instructorAvatar = instructor?.avatar?.sort(
    (a, b) => a.width - b.width
  )[0]?.src;

  // Update page meta when instructor data is available
  usePageMeta(instructorName, instructorAvatar);

  const initialInstructorData = useMemo(
    () =>
      isNewInstructor
        ? NEW_INSTRUCTOR_INITIAL_DATA
        : mapInstructorToDraftData(instructor),
    [isNewInstructor, instructor]
  );
  const instructorData = instructorDataDraft ?? initialInstructorData;

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data: Partial<Instructor>) => {
      if (isNewInstructor) {
        return POST(`/api/healthcare-companies/${hcc}/e/instructors`, data);
      } else {
        return PUT(
          `/api/healthcare-companies/${hcc}/e/instructors/${instructorId}`,
          data
        );
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSuccess: (data: any) => {
      if (isNewInstructor && data?.id) {
        // For new instructors, navigate to the created instructor's detail page
        queryClient.invalidateQueries({ queryKey: ["instructors", hcc] });
        navigate(`/e/${hcc}/instructors/${data.id}`);
      } else {
        queryClient.invalidateQueries({
          queryKey: ["instructor", hcc, instructorId],
        });
        queryClient.invalidateQueries({ queryKey: ["instructors", hcc] });
        setInstructorDataDraft(null);
      }
      setIsDirty(false);
      clearErrors();
    },
    onError: (error: unknown) => {
      if (error && typeof error === "object" && "response" in error) {
        const apiError = error as {
          response?: { data?: { errors?: Record<string, string> } };
        };
        if (apiError.response?.data?.errors) {
          // Set API errors
          Object.entries(apiError.response.data.errors).forEach(
            ([field, error]) => {
              setFieldError(field, error);
            }
          );
        }
      }
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () =>
      DELETE(`/api/healthcare-companies/${hcc}/e/instructors/${instructorId}`),
    onSuccess: () => {
      // Invalidate queries and navigate back to instructors list
      queryClient.invalidateQueries({ queryKey: ["event-instructors"] });

      navigate(`/e/${hcc}/instructors${location.search}`);
    },
  });

  // Handle data changes
  const handleDataChange = useCallback(
    (updates: Partial<Instructor>) => {
      const newInstructorData = { ...instructorData, ...updates };
      setInstructorDataDraft(newInstructorData);
      setIsDirty(true);

      // Validate each updated field in real-time
      Object.keys(updates).forEach((field) => {
        const error = validateField(
          field,
          updates[field as keyof Instructor],
          newInstructorData,
          selectedLanguages
        );
        setFieldError(field, error);
      });
    },
    [instructorData, selectedLanguages, validateField, setFieldError]
  );

  // Handle save
  const handleSave = useCallback(() => {
    // Validate all fields before saving
    const isValid = validate(instructorData, selectedLanguages);

    if (!isValid) {
      // Show toast notification
      const errorCount = Object.keys(fieldErrors).length;
      const translationKey =
        errorCount === 1
          ? "common.validation.requiredFieldsError_one"
          : "common.validation.requiredFieldsError_other";

      dispatch(
        showToast({
          message: t(translationKey, {
            count: errorCount,
            defaultValue: `Please fill in all required fields. ${errorCount} field${
              errorCount > 1 ? "s" : ""
            } need${errorCount > 1 ? "" : "s"} attention.`,
          }),
          type: "error",
        })
      );
      return;
    }

    // Filter out undefined values and clean the data before sending
    const cleanData = Object.entries(instructorData).reduce(
      (acc, [key, value]) => {
        if (value !== undefined && value !== null) {
          // Special handling for empty strings in optional fields
          if (key === "customButtonLink" && value === "") {
            // Don't include empty customButtonLink
            return acc;
          }
          (acc as Record<string, unknown>)[key] = value;
        }
        return acc;
      },
      {} as Partial<Instructor>
    );

    saveMutation.mutate({
      ...cleanData,
      placeId: instructorData.geoAddress?.googlePlaceId,
    });
  }, [
    instructorData,
    saveMutation,
    t,
    selectedLanguages,
    dispatch,
    validate,
    fieldErrors,
  ]);

  // Handle discard changes
  const handleDiscard = useCallback(() => {
    if (instructor) {
      setInstructorDataDraft(null);
      setIsDirty(false);
      clearErrors();
    }
  }, [instructor, clearErrors]);

  // Navigation handlers
  const handleBack = useCallback(() => {
    navigate(`/e/${hcc}/instructors${location.search}`);
  }, [navigate, hcc, location.search]);

  const instructorProfileURL = metadata?.instructorProfileURL;
  const handlePreview = useCallback(() => {
    if (instructorData.slug && instructorProfileURL) {
      window.open(
        `${instructorProfileURL}${instructorData.slug}`,
        "_blank"
      );
    }
  }, [instructorData.slug, instructorProfileURL]);

  const handleDelete = useCallback(() => {
    setShowDeleteModal(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    deleteMutation.mutate();
    setShowDeleteModal(false);
  }, [deleteMutation]);

  const handleCloseDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  if (isLoading && !isNewInstructor) {
    return <InstructorDetailsSkeleton />;
  }

  return (
    <AppPageWrapper>
      <DeleteConfirmationModal
        open={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        loading={deleteMutation.isPending}
      />
      {isDirty && (
        <ContextualSaveBar
          backgroundColor="#2B0069"
          saveAction={{
            content: isNewInstructor ? t("common.create") : t("common.save"),
            onAction: handleSave,
            loading: saveMutation.isPending,
          }}
          discardAction={
            !isNewInstructor
              ? {
                  onAction: handleDiscard,
                }
              : undefined
          }
        />
      )}

      {isFetching && <Loading />}

      <Page
        fullWidth
        backAction={{ content: t("common.back"), onAction: handleBack }}
        title={
          isNewInstructor
            ? t("instructorDetails.createNewInstructor")
            : instructorData.nameWithPrefix
        }
        primaryAction={
          !isNewInstructor
            ? {
                content: t("instructorDetails.actions.preview"),
                icon: ExternalIcon,
                onAction: handlePreview,
              }
            : undefined
        }
        secondaryActions={
          !isNewInstructor
            ? [
                {
                  content: t("instructorDetails.actions.delete"),
                  icon: DeleteIcon,
                  destructive: true,
                  onAction: handleDelete,
                },
              ]
            : undefined
        }
      >
        <Layout>
          <Layout.Section>
            <BlockStack gap="400">
              <OverviewCard
                instructorData={JSON.parse(JSON.stringify(instructorData))}
                onDataChange={handleDataChange}
                fieldErrors={fieldErrors}
              />

              <CourseLanguageSelectionCard
                instructorData={JSON.parse(JSON.stringify(instructorData))}
                onDataChange={handleDataChange}
                fieldErrors={fieldErrors}
              />

              {!isNewInstructor && (
                <ProfileImageAndMediaCard
                  instructorData={JSON.parse(JSON.stringify(instructorData))}
                  onDataChange={handleDataChange}
                  fieldErrors={fieldErrors}
                />
              )}

              <ContactInformationCard
                instructorData={JSON.parse(JSON.stringify(instructorData))}
                onDataChange={handleDataChange}
                fieldErrors={fieldErrors}
              />

              <SocialMediaProfilesCard
                instructorData={JSON.parse(JSON.stringify(instructorData))}
                onDataChange={handleDataChange}
                fieldErrors={fieldErrors}
              />

              <ProfileButtonCard
                instructorData={JSON.parse(JSON.stringify(instructorData))}
                onDataChange={handleDataChange}
                fieldErrors={fieldErrors}
              />

              <AffiliatedBrandsCard
                instructorData={JSON.parse(JSON.stringify(instructorData))}
                onDataChange={handleDataChange}
              />
            </BlockStack>
          </Layout.Section>
          <Layout.Section variant="oneThird">
            <Layout>
              <Layout.Section>
                <InstructorStatusCard
                  instructorData={JSON.parse(JSON.stringify(instructorData))}
                  onDataChange={handleDataChange}
                />
              </Layout.Section>

              <Layout.Section>
                <InstructorTranslationsCard
                  translationsExpanded={translationsExpanded}
                  setTranslationsExpanded={setTranslationsExpanded}
                  entityType={TranslationsEntityType.INSTRUCTOR}
                />
              </Layout.Section>

              <Layout.Section>
                <Card>
                  <InstructorNotes
                    notes={instructorData?.privateNote || ""}
                    tone={
                      (instructorData?.privateNoteTone as NoteTone) || "default"
                    }
                    loading={isLoading}
                    onDataChange={handleDataChange}
                    toneOptions={toneOptions}
                  />
                </Card>
              </Layout.Section>
            </Layout>
          </Layout.Section>
        </Layout>
      </Page>
    </AppPageWrapper>
  );
}
